import React, { ReactNode, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './SearchInput.styles';
import { Input } from '@/components/UI/Input/Input';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { SearchIcon } from '@/components/UI/Icons/Icons';

interface Props {
  onSearch: (value: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  hasBorderBottom?: boolean;
  hasPadding?: boolean;
  customPaddingTop?: number;
  customPaddingBottom?: number;
  leftIcon?: ReactNode;
}

export interface SearchInputRef {
  clear: () => void;
}

export const SearchInput = React.forwardRef<SearchInputRef, Props>(
  (
    {
      onSearch,
      placeholder,
      isLoading,
      hasBorderBottom = true,
      hasPadding = true,
      customPaddingTop,
      customPaddingBottom,
      leftIcon,
    },
    CategorySearchRef
  ) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [searchValue, setSearchValue] = useState('');

    function clear() {
      setSearchValue('');
      onSearch('');
    }

    function onChange(value: string) {
      setSearchValue(value);
      onSearch(value);
    }

    useImperativeHandle(CategorySearchRef, () => ({ clear }), []);

    return (
      <S.Container $hasPadding={hasPadding} $hasBorderBottom={hasBorderBottom}>
        {isLoading ? (
          <SkeletonLoader
            dataTestId="search-skeleton-loader"
            borderRadius={7}
            height="40px"
          />
        ) : (
          <Input
            type="search"
            value={searchValue}
            onChange={value => onChange(value as string)}
            appearance="outlined"
            customPaddingTop={customPaddingTop}
            customPaddingBottom={customPaddingBottom}
            placeholder={placeholder ?? t('common.search')}
            leftIcon={
              leftIcon ?? <SearchIcon fill={theme.palette.secondary.labels} />
            }
            clear={clear}
          />
        )}
      </S.Container>
    );
  }
);

SearchInput.displayName = 'SearchInput';
